import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiPlus, FiEdit2, FiTrash2, FiMenu, FiGrid, FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ProductForm from './ProductForm';
import { useStore } from '../../context/StoreContext';

const AdminProducts = () => {
  const { products, fetchProducts } = useStore();
  const [localProducts, setLocalProducts] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  const handleAdd = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleSubmit = async (productData) => {
    if (editingProduct) {
      await supabase
        .from('products')
        .update(productData)
        .eq('id', editingProduct.id);
    } else {
      productData.display_order = localProducts.length;
      await supabase.from('products').insert([productData]);
    }
    setIsFormOpen(false);
    fetchProducts();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await supabase.from('products').delete().eq('id', id);
      fetchProducts();
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = "move";
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newProducts = [...localProducts];
    const draggedItem = newProducts[draggedIndex];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(index, 0, draggedItem);
    
    setDraggedIndex(index);
    setLocalProducts(newProducts);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    const updates = localProducts.map((p, idx) => ({
      id: p.id,
      display_order: idx
    }));

    try {
      await Promise.all(
        updates.map(u => 
          supabase.from('products').update({ display_order: u.display_order }).eq('id', u.id)
        )
      );
      fetchProducts();
    } catch (err) {
      console.error("Failed to save reorder", err);
    }
  };

  const categoryCounts = localProducts.reduce((acc, product) => {
    const cat = product.category || 'Uncategorized';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  const displayProducts = categoryFilter
    ? localProducts.filter(p => (p.category || 'Uncategorized') === categoryFilter)
    : localProducts;

  const toggleCategoryFilter = (cat) => {
    setCategoryFilter(prev => prev === cat ? null : cat);
  };

  return (
    <div className="space-y-6">
      {/* Inventory Summary Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiGrid className="text-brand-gold" />
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-800">Inventory Summary</h3>
          </div>
          {categoryFilter && (
            <button
              onClick={() => setCategoryFilter(null)}
              className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-red-500 hover:text-red-600 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100 transition-all"
            >
              <FiX size={12} />
              Clear Filter
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {/* All Items Button */}
          <button
            onClick={() => setCategoryFilter(null)}
            className={`px-5 py-3 rounded-xl border min-w-[120px] text-left transition-all ${
              !categoryFilter
                ? 'bg-brand-sand/50 border-brand-darkSage/30 ring-2 ring-brand-gold/30 shadow-md'
                : 'bg-gray-50 border-gray-100 hover:border-brand-gold/50 hover:shadow-sm'
            }`}
          >
            <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${
              !categoryFilter ? 'text-brand-gold' : 'text-brand-lightText'
            }`}>All Items</p>
            <p className={`text-2xl font-serif leading-none ${
              !categoryFilter ? 'text-brand-darkSage' : 'text-gray-800'
            }`}>{localProducts.length}</p>
          </button>

          {/* Category Buttons */}
          {Object.entries(categoryCounts).map(([cat, count]) => {
            const isActive = categoryFilter === cat;
            return (
              <button
                key={cat}
                onClick={() => toggleCategoryFilter(cat)}
                className={`px-5 py-3 rounded-xl border min-w-[120px] text-left transition-all ${
                  isActive
                    ? 'bg-brand-sand/50 border-brand-darkSage/30 ring-2 ring-brand-gold/30 shadow-md'
                    : 'bg-gray-50 border-gray-100 hover:border-brand-gold/50 hover:shadow-sm'
                }`}
              >
                <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${
                  isActive ? 'text-brand-gold' : 'text-gray-500'
                }`}>{cat}</p>
                <p className={`text-2xl font-serif leading-none ${
                  isActive ? 'text-brand-darkSage' : 'text-gray-800'
                }`}>{count}</p>
              </button>
            );
          })}
        </div>

        {categoryFilter && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing <span className="font-bold text-brand-darkSage">{displayProducts.length}</span> product{displayProducts.length !== 1 ? 's' : ''} in <span className="font-bold text-brand-gold">{categoryFilter}</span>
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-serif text-gray-800">
              {categoryFilter ? `${categoryFilter} Products` : 'Products List'}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {categoryFilter
                ? `Filtered by category: ${categoryFilter}`
                : 'Drag and drop the 3 lines to reorder. The top 6 items will show in Section 2.'
              }
            </p>
          </div>
          <button 
            onClick={handleAdd}
            className="flex items-center gap-2 bg-brand-darkSage text-white px-4 py-2 rounded-xl text-sm hover:bg-brand-sage transition-colors whitespace-nowrap"
          >
            <SafeIcon icon={FiPlus} /> Add Product
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 text-gray-500 text-xs uppercase tracking-wider">
                {!categoryFilter && <th className="p-4 font-medium w-10"></th>}
                <th className="p-4 font-medium">Product</th>
                <th className="p-4 font-medium">Category</th>
                <th className="p-4 font-medium">Expiry Date</th>
                <th className="p-4 font-medium">Price</th>
                <th className="p-4 font-medium">Stock</th>
                <th className="p-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayProducts.length === 0 ? (
                <tr>
                  <td colSpan={categoryFilter ? 6 : 7} className="p-12 text-center text-gray-400 text-sm">
                    No products found in this category.
                  </td>
                </tr>
              ) : displayProducts.map((product, index) => (
                <tr 
                  key={product.id}
                  draggable={!categoryFilter}
                  onDragStart={(e) => !categoryFilter && handleDragStart(e, index)}
                  onDragOver={(e) => !categoryFilter && handleDragOver(e, index)}
                  onDragEnd={() => !categoryFilter && handleDragEnd()}
                  className={`hover:bg-gray-50/50 transition-colors ${draggedIndex === index ? 'opacity-50 bg-gray-100' : ''}`}
                >
                  {!categoryFilter && (
                    <td className="p-4 cursor-grab active:cursor-grabbing text-gray-400 hover:text-brand-darkSage w-10">
                      <FiMenu size={20} />
                    </td>
                  )}
                  <td className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 p-1 flex-shrink-0">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" draggable="false" />
                    </div>
                    <span className="font-semibold text-gray-800 line-clamp-1">
                      {product.name}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-gray-600">{product.category}</td>
                  <td className="p-4 text-sm text-gray-600">
                    {product.expiry_date ? new Date(product.expiry_date).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-4 text-sm font-medium text-gray-800">
                    Rs. {product.price.toFixed(2)}
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.stock_level > 10 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {product.stock_level}
                    </span>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button 
                      onClick={() => handleEdit(product)}
                      className="p-2 text-gray-400 hover:text-brand-darkSage transition-colors"
                      title="Edit Product"
                    >
                      <SafeIcon icon={FiEdit2} size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(product.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete Product"
                    >
                      <SafeIcon icon={FiTrash2} size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <ProductForm 
          product={editingProduct} 
          onSubmit={handleSubmit} 
          onClose={() => setIsFormOpen(false)} 
        />
      )}
    </div>
  );
};

export default AdminProducts;